import { useEffect } from 'react';
import { generatePath } from 'react-router-dom';
import { PUBLIC_MAP_ROUTE } from '~/_shared/constants/routes';
import { useMapInfoDataSelector } from '~/store/mapInfo/mapInfo.selectors';

const MANIFEST_LINK_SELECTOR = 'link[rel="manifest"]';

type WebManifestIcon = Readonly<{
  src: string;
}>;

type WebManifest = Readonly<{
  name?: string;
  short_name?: string;
  icons?: ReadonlyArray<WebManifestIcon>;
}>;

// the base manifest is static, so it's fetched at most once per page load
let baseManifestPromise: Promise<WebManifest | null> | null = null;

const isWebManifest = (value: unknown): value is WebManifest =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const fetchBaseManifest = (url: string): Promise<WebManifest | null> => {
  baseManifestPromise ??= fetch(url)
    .then(response => response.json())
    .then((manifest: unknown) => (isWebManifest(manifest) ? manifest : null))
    .catch(() => null);

  return baseManifestPromise;
};

// the link's href gets replaced while a public map is open, so the default one is kept on the element
const getDefaultManifestUrl = (manifestLink: HTMLLinkElement): string => {
  manifestLink.dataset.defaultHref ??= manifestLink.href;
  return manifestLink.dataset.defaultHref;
};

const createPublicMapManifest = (baseManifest: WebManifest, baseManifestUrl: string, mapUrl: string, mapName: string | undefined) => ({
  ...baseManifest,
  // the manifest is served from a data URL, so all URLs in it have to be absolute
  icons: baseManifest.icons?.map(icon => ({
    ...icon,
    src: new URL(icon.src, baseManifestUrl).href,
  })),
  id: mapUrl,
  start_url: mapUrl,
  scope: mapUrl,
  ...(mapName ? { name: mapName, short_name: mapName } : {}),
});

// The static manifest has `start_url: '/'`, so a public map installed to the home screen
// would open the public domain root instead of the map. This swaps in a manifest pointing to the current map.
export const usePublicMapWebManifest = (mapShareId: string) => {
  const mapName = useMapInfoDataSelector()?.name ?? undefined;

  useEffect(() => {
    const manifestLink = document.querySelector<HTMLLinkElement>(MANIFEST_LINK_SELECTOR);

    if (!manifestLink || !mapShareId || mapName === undefined) {
      return;
    }

    const defaultManifestUrl = getDefaultManifestUrl(manifestLink);
    const mapPath = generatePath(PUBLIC_MAP_ROUTE, { mapShareId });
    const mapUrl = new URL(mapPath, window.location.origin).href;
    let isCancelled = false;

    fetchBaseManifest(defaultManifestUrl).then((baseManifest) => {
      if (isCancelled || !baseManifest) {
        return;
      }

      const manifest = createPublicMapManifest(baseManifest, defaultManifestUrl, mapUrl, mapName);
      manifestLink.href = `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`;
    });

    return () => {
      isCancelled = true;
      manifestLink.href = defaultManifestUrl;
    };
  }, [mapName, mapShareId]);
};
